import { useSearchParams } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import { BRAND } from "@/data/content";

const MAP_SRC =
  "https://www.google.com/maps?q=Rajarhat+Bishnupur+No+1+Gram+Panchayat+Bhatenda+Rajarhat+Kolkata+700135&output=embed";

export default function Contact() {
  const [params] = useSearchParams();
  const isQuote = params.get("quote") === "1";

  return (
    <div data-testid="contact-page">
      <PageHero
        overline={isQuote ? "Request a Quote" : "Contact Us"}
        title={isQuote ? <>Let&apos;s talk <span className="text-gold-gradient italic">numbers.</span></> : <>Let&apos;s start a <span className="text-gold-gradient italic">conversation.</span></>}
        subtitle="Visit our showroom, call us, or send an enquiry. Our team is ready to help you with premium interior solutions."
        image="/photos/bedroom/19.webp"
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid lg:grid-cols-2 gap-14">
          {/* Left: details */}
          <div>
            <Reveal>
              <Overline>Showroom & Office</Overline>
              <h2 className="font-serif-lux mt-5 text-3xl md:text-4xl tracking-tight text-[#fafafa]">{BRAND.legal}</h2>
            </Reveal>

            <div className="mt-8 space-y-6">
              <Reveal>
                <div className="flex gap-4" data-testid="contact-address">
                  <MapPin size={20} className="text-[#e7c15a] shrink-0 mt-1" strokeWidth={1.5} />
                  <div className="text-[#c5c5c5] text-sm leading-relaxed">
                    {BRAND.address.lines.map((l) => <div key={l}>{l}</div>)}
                    <div className="text-[#e7c15a]/80 mt-2">Landmark: {BRAND.address.landmark}</div>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <div className="flex gap-4">
                  <Phone size={20} className="text-[#e7c15a] shrink-0 mt-1" strokeWidth={1.5} />
                  <div className="text-sm">
                    {BRAND.phones.map((p, i) => (
                      <a key={p} href={`tel:${BRAND.phoneRaw[i]}`} className="block text-[#e8e8e8] hover:text-[#e7c15a] transition-colors">{p}</a>
                    ))}
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="flex gap-4">
                  <Mail size={20} className="text-[#e7c15a] shrink-0 mt-1" strokeWidth={1.5} />
                  <a href={`mailto:${BRAND.email}`} className="text-sm text-[#e8e8e8] hover:text-[#e7c15a] transition-colors">{BRAND.email}</a>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="flex gap-4">
                  <Clock size={20} className="text-[#e7c15a] shrink-0 mt-1" strokeWidth={1.5} />
                  <div className="text-sm text-[#c5c5c5]">Mon – Sat: 10:00 AM – 8:00 PM<br />Sunday: By appointment</div>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.2}>
              <div className="flex gap-3 mt-8">
                <a href={BRAND.social.instagram} target="_blank" rel="noreferrer" className="h-11 w-11 grid place-items-center border border-white/10 text-[#e7c15a] hover:bg-[#e7c15a] hover:text-[#050505] transition-colors"><Instagram size={18} strokeWidth={1.5} /></a>
                <a href={BRAND.social.facebook} target="_blank" rel="noreferrer" className="h-11 w-11 grid place-items-center border border-white/10 text-[#e7c15a] hover:bg-[#e7c15a] hover:text-[#050505] transition-colors"><Facebook size={18} strokeWidth={1.5} /></a>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="mt-10 border border-white/10 overflow-hidden">
                <iframe
                  title="Vernika Designs Location"
                  data-testid="contact-map"
                  src={MAP_SRC}
                  width="100%"
                  height="320"
                  style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg) contrast(0.9)" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <Reveal delay={0.1}>
            <div className="border border-white/10 bg-[#0a0a0a] p-7 md:p-10 lg:sticky lg:top-28">
              <Overline>{isQuote ? "Quote Request Form" : "Enquiry Form"}</Overline>
              <h3 className="font-serif-lux mt-4 mb-7 text-3xl text-[#fafafa]">
                {isQuote ? "Get your custom quote" : "Send us a message"}
              </h3>
              <EnquiryForm source={isQuote ? "quote" : "contact"} />
              <p className="mt-5 text-xs text-[#666] text-center">We typically respond within one business day.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
